/**
 * Onboarding Page
 * 
 * A 3-step onboarding flow guiding users through:
 * - Step 1: Connect Google Calendar
 * - Step 2: Create Automation Rules
 * - Step 3: Setup Analytics
 */

import { useState, useCallback } from 'react'
import { useOnboarding, type OnboardingStepStatus } from '@/context/onboarding-context'
import { useCalendars } from '@/context/calendars-context'
import { CalendarIntegrationsDialog } from '@/components/integrations/calendar-integrations-dialog'
import { AutomationRulesDialog } from '@/components/automation-rules/automation-rules-dialog'
import { AnalyticsDialog } from '@/components/settings/analytics-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CalendarsIcon, LightningIcon, InsightsIcon, Check } from '@/components/icons'
import { Loader } from '@/components/ai-elements/loader'
import { cn } from '@/lib/utils'

// Step configuration
const STEPS = [
  {
    id: 1,
    label: 'Connect Calendar',
    title: 'Connect your Google Calendar',
    description: 'Link your calendar account to enable automation rules and analytics tracking.',
    icon: CalendarsIcon,
  },
  {
    id: 2,
    label: 'Automation Rules',
    title: 'Create automation rules',
    description: 'Set up rules to automatically color-code your calendar events based on patterns.',
    icon: LightningIcon,
  },
  {
    id: 3,
    label: 'Setup Analytics',
    title: 'Setup analytics tracking',
    description: 'Configure how your calendar events map to life domains for time insights.',
    icon: InsightsIcon,
  },
] as const

// Progress Bar Component
interface OnboardingProgressBarProps {
  currentStep: 1 | 2 | 3
  stepStatuses: {
    step1: OnboardingStepStatus
    step2: OnboardingStepStatus
    step3: OnboardingStepStatus
  }
}

function OnboardingProgressBar({ currentStep, stepStatuses }: OnboardingProgressBarProps) {
  const getStepStatus = (stepId: 1 | 2 | 3) => {
    const status = stepStatuses[`step${stepId}` as keyof typeof stepStatuses]
    if (status === 'done') return 'done'
    if (status === 'skipped') return 'skipped'
    if (stepId === currentStep) return 'active'
    return 'pending'
  }

  return (
    <div className="w-full max-w-xl mx-auto px-4">
      {/* Steps Container */}
      <div className="flex items-center justify-between relative">
        {/* Connecting Lines (behind circles) */}
        <div className="absolute top-5 left-0 right-0 flex justify-between px-[52px] md:px-[68px]">
          <div 
            className={cn(
              "h-0.5 flex-1",
              getStepStatus(1) !== 'pending' ? "bg-primary" : "bg-muted"
            )}
          />
          <div 
            className={cn(
              "h-0.5 flex-1",
              getStepStatus(2) !== 'pending' ? "bg-primary" : "bg-muted"
            )}
          />
        </div>

        {/* Step Circles */}
        {STEPS.map((step) => {
          const status = getStepStatus(step.id as 1 | 2 | 3)
          
          return (
            <div key={step.id} className="flex flex-col items-center z-10">
              {/* Circle */}
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                  status === 'done' && "bg-primary text-primary-foreground",
                  status === 'skipped' && "bg-muted text-muted-foreground",
                  status === 'active' && "bg-background border-2 border-primary text-primary",
                  status === 'pending' && "bg-background border-2 border-muted text-muted-foreground"
                )}
              >
                {status === 'done' ? (
                  <Check className="h-5 w-5" />
                ) : status === 'skipped' ? (
                  <span className="text-lg">—</span>
                ) : (
                  step.id
                )}
              </div>
              
              {/* Label */}
              <span 
                className={cn(
                  "mt-2 text-xs md:text-sm text-center max-w-[80px] md:max-w-[100px]",
                  status === 'active' ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Step Card Component
interface OnboardingStepCardProps {
  step: typeof STEPS[number]
  status: 'active' | 'done' | 'skipped' | 'pending'
  onContinue: () => void
  onSkip: () => void
  isLoading?: boolean
}

function OnboardingStepCard({ step, status, onContinue, onSkip, isLoading }: OnboardingStepCardProps) {
  const Icon = step.icon
  
  if (status !== 'active') {
    return null
  }

  return (
    <Card className="w-full max-w-md mx-auto border shadow-sm">
      <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="h-8 w-8 text-primary" />
        </div>
        
        {/* Title & Description */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">{step.title}</h2>
          <p className="text-muted-foreground text-sm">{step.description}</p>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col w-full gap-2 pt-2">
          <Button 
            onClick={onContinue} 
            disabled={isLoading}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader size={16} className="mr-2" />
                Loading...
              </>
            ) : (
              'Continue'
            )}
          </Button>
          <Button 
            variant="ghost" 
            onClick={onSkip}
            disabled={isLoading}
            className="w-full text-muted-foreground"
          >
            Skip for now
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// Main Onboarding Page
export function OnboardingPage() {
  const { onboarding, loading, currentStep, completeStep, skipStep } = useOnboarding()
  const { integrations } = useCalendars()
  
  // Dialog states
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false)
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false)
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false)
  
  // Track if action was taken in dialogs
  const [actionTakenInDialog, setActionTakenInDialog] = useState(false)

  // Note: Redirect for already-onboarded users is handled in ProtectedRoute
  // to ensure proper splash screen flow

  // Handle Continue button clicks
  const handleContinue = useCallback((step: 1 | 2 | 3) => {
    setActionTakenInDialog(false)
    
    switch (step) {
      case 1:
        setCalendarDialogOpen(true)
        break
      case 2:
        setAutomationDialogOpen(true)
        break
      case 3:
        setAnalyticsDialogOpen(true)
        break
    }
  }, [])

  // Handle Skip button clicks
  const handleSkip = useCallback(async (step: 1 | 2 | 3) => {
    await skipStep(step)
  }, [skipStep])

  // Handle Calendar Dialog close
  const handleCalendarDialogClose = useCallback(async () => {
    setCalendarDialogOpen(false)
    
    // Check if user connected a calendar (either through action callback or by checking integrations)
    if (actionTakenInDialog || integrations.length > 0) {
      await completeStep(1)
    }
    setActionTakenInDialog(false)
  }, [actionTakenInDialog, integrations.length, completeStep])

  // Handle Calendar connected (called from dialog onSuccess)
  const handleCalendarConnected = useCallback(() => {
    setActionTakenInDialog(true)
  }, [])

  // Handle Automation Dialog close
  const handleAutomationDialogClose = useCallback(async () => {
    setAutomationDialogOpen(false)
    
    if (actionTakenInDialog) {
      await completeStep(2)
    }
    setActionTakenInDialog(false)
  }, [actionTakenInDialog, completeStep])

  // Handle Rule created (called from dialog onRuleCreated)
  const handleRuleCreated = useCallback(() => {
    setActionTakenInDialog(true)
  }, [])

  // Handle Analytics Dialog close
  const handleAnalyticsDialogClose = useCallback(async () => {
    setAnalyticsDialogOpen(false)
    
    if (actionTakenInDialog) {
      await completeStep(3)
    }
    setActionTakenInDialog(false)
  }, [actionTakenInDialog, completeStep])

  // Handle Analytics saved (called from dialog onSuccess)
  const handleAnalyticsSaved = useCallback(() => {
    setActionTakenInDialog(true)
  }, [])

  // Get step status
  const getStepStatus = (stepId: 1 | 2 | 3): 'active' | 'done' | 'skipped' | 'pending' => {
    if (!onboarding) return 'pending'
    const status = onboarding[`step${stepId}` as keyof typeof onboarding]
    if (status === 'done') return 'done'
    if (status === 'skipped') return 'skipped'
    if (stepId === currentStep) return 'active'
    return 'pending'
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader size={32} />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8">
      {/* Header */}
      <header className="mb-8 text-center">
        <h1 className="text-2xl font-bold">Welcome to Calintent</h1>
        <p className="text-muted-foreground mt-1">Let's get you set up in a few quick steps</p>
        <p className="text-xs text-muted-foreground mt-2">
          You can always change these settings later in the Settings page.
        </p>
      </header>

      {/* Progress Bar */}
      <div className="w-full mb-8">
        {onboarding && (
          <OnboardingProgressBar 
            currentStep={currentStep} 
            stepStatuses={{
              step1: onboarding.step1,
              step2: onboarding.step2,
              step3: onboarding.step3,
            }} 
          />
        )}
      </div>

      {/* Main Content */}
      <main className="w-full max-w-md">
        {STEPS.map((step) => (
          <OnboardingStepCard
            key={step.id}
            step={step}
            status={getStepStatus(step.id as 1 | 2 | 3)}
            onContinue={() => handleContinue(step.id as 1 | 2 | 3)}
            onSkip={() => handleSkip(step.id as 1 | 2 | 3)}
          />
        ))}
      </main>

      {/* Dialogs - rendered outside main content flow */}
      <CalendarIntegrationsDialog
        open={calendarDialogOpen}
        onClose={handleCalendarDialogClose}
        onSuccess={handleCalendarConnected}
      />
      
      <AutomationRulesDialog
        open={automationDialogOpen}
        onClose={handleAutomationDialogClose}
        onRuleCreated={handleRuleCreated}
      />
      
      <AnalyticsDialog
        open={analyticsDialogOpen}
        onClose={handleAnalyticsDialogClose}
        onSuccess={handleAnalyticsSaved}
      />
    </div>
  )
}

